import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"

/**
 * Canonical, deterministic date / time / number formatting.
 *
 * WHY THIS EXISTS. Scattered `new Date(x).toLocaleString()` calls render
 * differently across JS runtimes — iOS WebKit's ICU joins date + time with
 * " at " (and uses D MMM / 24h), while Node/Chromium (Storybook + the
 * addon-vitest run) join with ", " (and use M/D / 12h). That made Storybook
 * diverge from the phone, made story-tests lock in a baseline the phone never
 * matches, and made every label non-deterministic per user device/region in
 * production. These helpers instead:
 *   1. format via Lingui's `i18n` pinned to the ACTIVE APP locale (en/nl), and
 *   2. format the date and the time as SEPARATE Intl parts joined by a
 *      *translatable* connector — sidestepping the ICU date+time-pattern that
 *      varies by engine.
 * Result: identical output across every runtime for a given app locale, and the
 * label follows the user's chosen language. Every date/time/number shown to a
 * user should go through here (admin/diagnostics included), not `toLocale*`.
 */

function toValidDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

interface IDateOptions {
  /** Include the year in the date part. Defaults to true. */
  year?: boolean
}

function dateParts(year: boolean): Intl.DateTimeFormatOptions {
  return year ? { day: "numeric", month: "short", year: "numeric" } : { day: "numeric", month: "short" }
}

const TIME_PARTS: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" }
const TIME_PARTS_WITH_SECONDS: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" }

/**
 * Date + time, e.g. "May 6, 2026 at 04:00 PM" (en) / "6 mei 2026 om 16:00" (nl).
 * Pass `{ year: false }` for the compact "May 6 at 04:00 PM" used in ephemeral
 * headers (the live recording topbar). Returns "" for nullish/invalid input.
 */
export function formatDateTime(
  i18n: I18n,
  value: Date | string | number | null | undefined,
  options: IDateOptions = {},
): string {
  const d = toValidDate(value)
  if (!d) return ""
  const datePart = i18n.date(d, dateParts(options.year !== false))
  const timePart = i18n.date(d, TIME_PARTS)
  return i18n._(msg`${datePart} at ${timePart}`)
}

/** Date only, e.g. "May 6, 2026" (or "May 6" with `{ year: false }`). */
export function formatDate(
  i18n: I18n,
  value: Date | string | number | null | undefined,
  options: IDateOptions = {},
): string {
  const d = toValidDate(value)
  if (!d) return ""
  return i18n.date(d, dateParts(options.year !== false))
}

/** Compact date with no year, e.g. "May 6" — for dense cards/lists. */
export function formatShortDate(i18n: I18n, value: Date | string | number | null | undefined): string {
  return formatDate(i18n, value, { year: false })
}

/** Time only, e.g. "04:00 PM" (en) / "16:00" (nl). */
export function formatTime(i18n: I18n, value: Date | string | number | null | undefined): string {
  const d = toValidDate(value)
  if (!d) return ""
  return i18n.date(d, TIME_PARTS)
}

/**
 * Time only, with seconds, e.g. "04:00:12 PM" (en) / "16:00:12" (nl) — for dense
 * diagnostic timelines where second-granularity matters for event ordering.
 */
export function formatTimeWithSeconds(i18n: I18n, value: Date | string | number | null | undefined): string {
  const d = toValidDate(value)
  if (!d) return ""
  return i18n.date(d, TIME_PARTS_WITH_SECONDS)
}

/** Integer with locale grouping, e.g. "12,345" (en) / "12.345" (nl). */
export function formatInteger(i18n: I18n, n: number): string {
  return i18n.number(n, { maximumFractionDigits: 0 })
}

export interface IRelativeOptions {
  /**
   * What to return for an age below one minute. Defaults to a Lingui
   * `just now`. Pass `"seconds"` to render `${n}s ago` (live freshness UIs that
   * want second-granularity), or a literal string to override entirely.
   */
  subMinute?: "just-now" | "seconds"
  /**
   * Coarsest mode: when `true`, ages are bucketed at DAY granularity with
   * `today` / `yesterday` / `${n} days ago` (used by the campaign card, where
   * sub-day precision is noise). Sub-day ages collapse to `today`.
   */
  dayGranular?: boolean
}

/**
 * Lingui-aware "time ago" label from an elapsed duration in milliseconds.
 *
 * One ladder — seconds → minutes → hours → days → weeks → months — replacing the
 * four hand-rolled variants that had forked thresholds and (worse) hardcoded
 * English inside otherwise-translated tooltips. Callers compute the elapsed ms
 * (`Date.now() - then`, or `seconds * 1000`) and pick the sub-minute behaviour;
 * the unit words + pluralisation come from the active app locale.
 *
 * Negative input clamps to 0 (treated as "just now" / "today").
 */
export function formatRelative(i18n: I18n, elapsedMs: number, options: IRelativeOptions = {}): string {
  const ms = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (options.dayGranular) {
    if (days < 1) return i18n._(msg`today`)
    if (days === 1) return i18n._(msg`yesterday`)
    if (days < 7) return i18n._(msg`${days} days ago`)
    if (days < 30) return i18n._(msg`${Math.floor(days / 7)}w ago`)
    return i18n._(msg`${Math.floor(days / 30)}mo ago`)
  }

  if (seconds < 60) {
    if (options.subMinute === "seconds") return i18n._(msg`${seconds}s ago`)
    return i18n._(msg`just now`)
  }
  if (minutes < 60) return i18n._(msg`${minutes}m ago`)
  if (hours < 24) return i18n._(msg`${hours}h ago`)
  if (days < 7) return i18n._(msg`${days}d ago`)
  if (days < 30) return i18n._(msg`${Math.floor(days / 7)}w ago`)
  return i18n._(msg`${Math.floor(days / 30)}mo ago`)
}

/**
 * Elapsed-time clock as `mm:ss` — for recording timers, transcript timestamps,
 * countdowns and finalization elapsed.
 *
 * WHY mm:ss (zero-padded minutes) and not m:ss: a fixed two-digit minute field
 * keeps the timer width stable as it ticks past 9:59 → 10:00, so the UI doesn't
 * jump a column. This is a DURATION, not a wall-clock time, so it is locale-
 * independent and takes no `i18n` — there is one correct rendering of "5 min 7 s"
 * as a stopwatch. Negative / non-finite input clamps to `00:00` (the guard the
 * seven scattered copies each re-implemented). Minutes are NOT capped at 59;
 * a 75-minute session reads `75:00`, not `01:15:00`.
 */
export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00"
  const total = Math.floor(seconds)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
}

/**
 * Normalise a Postgres timestamp string into a value `new Date()` parses to the
 * CORRECT instant. Postgres emits e.g. `2026-05-29 16:52:20.545863+02` — a
 * space separator and a bare numeric offset. V8 returns `Invalid Date` for
 * `+02` (it needs `+02:00`), and appending a `Z` (the old bug) silently DROPS
 * the offset and yields an instant 2h wrong. We swap the space for `T` and
 * colonise the trailing offset. Strings already in ISO-with-offset / `…Z` form
 * pass through unchanged. Returns null for nullish/unparseable input.
 */
export function parsePgTimestamp(ts: string | null | undefined): Date | null {
  if (!ts) return null
  const withT = ts.trim().replace(" ", "T")
  // +02 → +02:00, -0530 → -05:30; leaves "+02:00" and "…Z" untouched.
  const normalised = withT.replace(/([+-])(\d{2})(\d{2})?$/, (_m, sign: string, hh: string, mm?: string) => {
    return `${sign}${hh}:${mm ?? "00"}`
  })
  const d = new Date(normalised)
  return Number.isNaN(d.getTime()) ? null : d
}
